import { Link } from 'react-router-dom';
import { HelpCircle, ArrowLeft, Mail, Shield, Clock, RefreshCw, Lock, Trash2, AlertTriangle, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Footer } from '@/components/Footer';
import { Navbar } from '@/components/Navbar';
import { AdBanner } from '@/components/AdBanner';
import { ResponsiveAdBanner } from '@/components/ResponsiveAdBanner';
import { Helmet } from 'react-helmet-async';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const FAQ = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What is TempVault?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "TempVault is a free, secure disposable email service that provides you with a temporary email address instantly. No registration or personal information required."
        }
      },
      {
        "@type": "Question",
        "name": "How long do temporary emails last?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "All temporary email addresses and their contents automatically expire and are permanently deleted after 24 hours for maximum privacy and security."
        }
      },
      {
        "@type": "Question",
        "name": "Is TempVault free to use?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, TempVault is completely free to use. The service is supported by advertisements, allowing us to provide this privacy tool at no cost to users."
        }
      },
      {
        "@type": "Question",
        "name": "Can I send emails from TempVault?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "No, TempVault is a receive-only email service. You can only receive emails to your temporary address, not send them."
        }
      },
      {
        "@type": "Question",
        "name": "Is my data secure with TempVault?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, we prioritize your privacy. We don't require any personal information, all emails are automatically deleted after 24 hours, and we use secure encrypted connections."
        }
      }
    ]
  };

  const faqCategories = [
    {
      title: "Getting Started",
      icon: Mail,
      questions: [
        {
          q: "What is TempVault?",
          a: "TempVault is a free, secure disposable email service that provides you with a temporary email address instantly. It's perfect for signing up for websites, receiving verification codes, or any situation where you want to protect your real email address from spam and unwanted messages."
        },
        {
          q: "How do I get a temporary email address?",
          a: "Simply visit TempVault and you'll automatically receive a unique temporary email address. No registration, no personal information required. Your inbox is ready to use immediately."
        },
        {
          q: "Is TempVault completely free?",
          a: "Yes, TempVault is 100% free to use. We're supported by advertisements, which allows us to provide this privacy-focused service at no cost to you."
        },
        {
          q: "Do I need to create an account?",
          a: "No account creation is needed. TempVault works instantly without any registration. Just visit the site and start using your temporary email right away."
        }
      ]
    },
    {
      title: "Email Features",
      icon: RefreshCw,
      questions: [
        {
          q: "How long do emails stay in my inbox?",
          a: "All emails and temporary addresses automatically expire and are permanently deleted after 24 hours. This ensures maximum privacy and security for all users."
        },
        {
          q: "Can I send emails from my temporary address?",
          a: "No, TempVault is a receive-only service. You can receive emails to your temporary address but cannot send emails from it. This is by design to prevent misuse."
        },
        {
          q: "Can I receive attachments?",
          a: "TempVault supports receiving emails with attachments. However, for security reasons, we recommend caution when opening attachments from unknown senders."
        },
        {
          q: "How quickly do I receive emails?",
          a: "Emails are delivered to your TempVault inbox in real-time. As soon as an email is sent to your temporary address, it will appear in your inbox within seconds."
        },
        {
          q: "Can I get a new email address?",
          a: "Yes! Click the 'Generate New' button to get a fresh temporary email address. Your old inbox and all its emails will be discarded, and you'll start with a clean slate."
        }
      ]
    },
    {
      title: "Privacy & Security",
      icon: Shield,
      questions: [
        {
          q: "Is my data secure?",
          a: "Absolutely. We prioritize your privacy above all else. We don't collect personal information, use encrypted connections (HTTPS), and automatically delete all data after 24 hours."
        },
        {
          q: "Do you store my emails permanently?",
          a: "No. All emails are automatically and permanently deleted after 24 hours. We don't keep backups or archives of your messages."
        },
        {
          q: "Can others see my emails?",
          a: "Your inbox is protected by a unique token that only you possess. Only someone with your specific inbox URL can access your emails."
        },
        {
          q: "Do you track users?",
          a: "We use minimal analytics to improve our service and display relevant advertisements. We never sell personal data and don't track your email content."
        },
        {
          q: "Is the connection encrypted?",
          a: "Yes, all connections to TempVault use HTTPS encryption to protect your data in transit."
        }
      ]
    },
    {
      title: "Common Use Cases",
      icon: CheckCircle,
      questions: [
        {
          q: "What can I use TempVault for?",
          a: "TempVault is perfect for: signing up for newsletters, creating accounts on websites you don't fully trust, receiving one-time verification codes, testing email functionality in apps you're developing, and avoiding spam in your personal inbox."
        },
        {
          q: "Can I use TempVault for important accounts?",
          a: "We recommend using TempVault only for temporary needs. Since emails are deleted after 24 hours, don't use it for accounts you need long-term access to, like banking or social media accounts."
        },
        {
          q: "Is TempVault good for receiving verification codes?",
          a: "Yes! TempVault is excellent for receiving one-time verification codes (OTPs). We even automatically detect and highlight OTP codes in emails to make them easy to find and copy."
        }
      ]
    },
    {
      title: "Troubleshooting",
      icon: AlertTriangle,
      questions: [
        {
          q: "Why am I not receiving emails?",
          a: "Some websites block disposable email addresses. If you're not receiving emails: 1) Wait a few moments as there may be a slight delay, 2) Check that you copied the email address correctly, 3) Try generating a new address if the sender blocks disposable emails."
        },
        {
          q: "My inbox expired. Can I recover my emails?",
          a: "Unfortunately, no. Once the 24-hour period expires, all data is permanently deleted with no possibility of recovery. This is by design to protect user privacy."
        },
        {
          q: "The email looks broken or incomplete",
          a: "Some emails may not render perfectly due to complex formatting. Try viewing the email in plain text mode if available, or check if the essential information (like verification codes) is still visible."
        },
        {
          q: "Can I extend my inbox duration?",
          a: "Currently, all inboxes have a fixed 24-hour lifespan that cannot be extended. This policy ensures privacy and prevents abuse of the service."
        }
      ]
    }
  ];

  return (
    <>
      <Helmet>
        <title>FAQ - Frequently Asked Questions | TempVault</title>
        <meta name="description" content="Find answers to common questions about TempVault's free disposable email service. Learn how to use temporary emails, privacy features, and troubleshooting tips." />
        <meta name="keywords" content="TempVault FAQ, disposable email questions, temporary email help, temp mail guide" />
        <meta property="og:title" content="FAQ - TempVault Disposable Email" />
        <meta property="og:description" content="Frequently asked questions about using TempVault's free temporary email service." />
        <link rel="canonical" href="https://www.securemailws.com/faq" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <div className="min-h-screen gradient-surface gradient-mesh flex flex-col">
        <Navbar />
      
        <main className="flex-1 container mx-auto px-4 py-8 max-w-4xl">
          <div className="glass-strong rounded-3xl p-6 lg:p-10 glow-border">
            <div className="flex items-center gap-4 mb-8">
              <Link to="/">
                <Button variant="ghost" size="icon" className="rounded-full">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
              <div className="w-12 h-12 rounded-2xl gradient-bg flex items-center justify-center">
                <HelpCircle className="w-6 h-6 text-primary-foreground" />
              </div>
              <div>
                <h1 className="text-3xl font-bold gradient-text">Frequently Asked Questions</h1>
                <p className="text-muted-foreground">Everything you need to know about TempVault</p>
              </div>
            </div>

            {/* Quick Answer Cards */}
            <div className="grid gap-4 md:grid-cols-3 mb-8">
              <div className="glass rounded-2xl p-4 text-center">
                <Clock className="w-8 h-8 mx-auto mb-2 text-primary" />
                <h3 className="font-semibold">24 Hour Inbox</h3>
                <p className="text-sm text-muted-foreground">Auto-deletes for privacy</p>
              </div>
              <div className="glass rounded-2xl p-4 text-center">
                <Lock className="w-8 h-8 mx-auto mb-2 text-primary" />
                <h3 className="font-semibold">No Registration</h3>
                <p className="text-sm text-muted-foreground">Instant, anonymous access</p>
              </div>
              <div className="glass rounded-2xl p-4 text-center">
                <Trash2 className="w-8 h-8 mx-auto mb-2 text-primary" />
                <h3 className="font-semibold">100% Free</h3>
                <p className="text-sm text-muted-foreground">No hidden costs</p>
              </div>
            </div>

            {/* Ad Banners */}
            <div className="space-y-3 mb-8">
              <div className="glass rounded-2xl p-4">
                <AdBanner adFormat="horizontal" />
              </div>
              <div className="glass rounded-2xl p-4">
                <ResponsiveAdBanner variant="stacked" />
              </div>
            </div>

            {/* FAQ Accordion Sections */}
            <div className="space-y-6">
              {faqCategories.map((category, categoryIndex) => (
                <section key={category.title} className="glass rounded-2xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl gradient-bg flex items-center justify-center">
                      <category.icon className="w-5 h-5 text-primary-foreground" />
                    </div>
                    <h2 className="text-xl font-bold">{category.title}</h2>
                  </div>
                  
                  <Accordion type="single" collapsible className="w-full">
                    {category.questions.map((item, index) => (
                      <AccordionItem 
                        key={index} 
                        value={`${categoryIndex}-${index}`}
                        className="border-border/40"
                      >
                        <AccordionTrigger className="text-left hover:no-underline py-4">
                          <span className="font-medium">{item.q}</span>
                        </AccordionTrigger>
                        <AccordionContent className="text-muted-foreground pb-4 leading-relaxed">
                          {item.a}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </section>
              ))}
            </div>

            {/* Ad Banners */}
            <div className="space-y-3 my-8">
              <div className="glass rounded-2xl p-4">
                <AdBanner adFormat="horizontal" />
              </div>
              <div className="glass rounded-2xl p-4">
                <ResponsiveAdBanner variant="single" />
              </div>
            </div>

            {/* Still Have Questions */}
            <section className="glass rounded-2xl p-6 text-center">
              <h2 className="text-xl font-bold mb-3">Still Have Questions?</h2>
              <p className="text-muted-foreground mb-4">
                Can't find what you're looking for? We're here to help!
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/contact">
                  <Button className="rounded-full px-8">
                    Contact Us
                  </Button>
                </Link>
                <Link to="/">
                  <Button variant="outline" className="rounded-full px-8">
                    Try TempVault Now
                  </Button>
                </Link>
              </div>
            </section>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default FAQ;
